import React from "react";
import { useRouter } from "next/router";
import Image from "@Components/Image";

import { convertToDollar } from "@Utils/currency";

import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import classes from "./SearchInputPredictProductCA.module.scss";

export interface Product {
  name: string;
  description: string;
  sku: string;
  slug: string;
  categories: string[];
  subCategories: string[];
  price: Price[];
  badges: null;
  grade: string;
  cut: null;
  programDescription: null;
  maximumQuantity: null;
  image: Image;
  otherImage: Image;
  nutritionFactsImage: Image;
  popularity: number;
  attributes: Attribute[];
  isOnStock: boolean;
  productType: string;
  objectID: string;
  _highlightResult: HighlightResult;
  _rankingInfo: RankingInfo;
}

export interface HighlightResult {
  name: Grade;
  slug: Grade;
  categories: Grade[];
  subCategories: Grade[];
  grade: Grade;
}

export interface Grade {
  value: string;
  matchLevel: string;
  fullyHighlighted?: boolean;
  matchedWords: string[];
}

export interface RankingInfo {
  nbTypos: number;
  firstMatchedWord: number;
  proximityDistance: number;
  userScore: number;
  geoDistance: number;
  geoPrecision: number;
  nbExactWords: number;
  words: number;
  filters: number;
}

export interface Attribute {
  name: string;
  value: ValueElement[] | PurpleValue | number | string;
}

export interface ValueElement {
  key: string;
  label: LabelClass;
}

export interface LabelClass {
  "en-CA": string;
  en: string;
  fr: string;
  "fr-CA": string;
}

export interface PurpleValue {
  key?: string;
  label?: LabelClass | string;
  centAmount?: number;
  currencyCode?: string;
  fractionDigits?: number;
  type?: string;
}

export interface Image {
  url: null | string;
  title: null | string;
  width: number | null;
  height: number | null;
}

export interface Price {
  id: string;
  key: null;
  value: PriceValue;
  country: null;
  customerGroup: null;
  channel: null;
  validFrom: Date;
  validUntil: Date;
  discounted: null;
  tiers: null;
  custom: null;
}

export interface PriceValue {
  centAmount: number;
  currencyCode: string;
  fractionDigits: number;
  type: string;
}

interface Props {
  productList: Product[];
  activeItem: string;
}
export default function SearchInputPredictProductCA(props: Props) {
  const { productList, activeItem } = props;
  const router = useRouter();
  const { languageCode } = useAppSelector(localeSelector);

  /**
   * The function `handleOnProductClick` navigates to a specific product page based on the provided
   * slug.
   * @param {string} slug - The `slug` parameter is a string that represents a unique identifier for a
   * product. It is typically used in URLs to identify a specific product.
   */
  const handleOnProductClick = (slug: string) => {
    router.push(`/${languageCode}/products/${slug}`);
  };

  return (
    <div className={classes["product-container"]}>
      <div className={classes["product-header"]}>
        {productList?.length > 0 && (
          <span>
            <strong>
              Top Search For&nbsp;:&nbsp;&nbsp;
              <em>{activeItem}</em>
            </strong>
          </span>
        )}
      </div>
      <div className={classes["product-image-container"]}>
        {productList.map((product: any) => {
          return (
            <div
              className={classes["product-item"]}
              key={product?.name}
              onClick={() => handleOnProductClick(product?.slug)}
            >
              <div className={classes["product-image"]}>
                {product?.image?.url ? (
                  <Image
                    alt={product?.name}
                    src={
                      product?.image?.url &&
                      `${product.image.url}?fm=webp&w=250&h=250`
                    }
                    height={120}
                    width={120}
                  />
                ) : (
                  <Image
                    alt={product?.name}
                    src={`https://placehold.co/200x200?text=${product.name}`}
                    height={120}
                    width={120}
                  />
                  //NOTE: IN CASE OF OUT OF STOCK NEED TO ENABLE BELOW SNIPPET
                  // <div className={classes["badge-container"]}>
                  //   <Badge type={"badge--out-of-stock"}>
                  //     {t("products.badges.coming_soon")}
                  //   </Badge>
                  // </div>
                )}
              </div>
              <div className={classes["product-info"]}>
                <span className={classes["product-text-wrapper"]}>
                  <strong>{product?.name}</strong>
                </span>
                <span>
                  <strong>
                    ${convertToDollar(product?.price[0]?.value?.centAmount)}
                  </strong>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
